import { Component, signal, inject, computed, Input, AfterViewInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxInteractiveOrgChart, NgxInteractiveOrgChartTheme, OrgChartNode } from 'ngx-interactive-org-chart';
import { ThemeService } from '../../services/theme.service';
import { DocumentoService } from '../../services/documento.service';
import { environment } from '../../../environments/environment';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { EstadoTareaPipe } from '../../pipes/estado-tarea.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, map } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

interface ITarea {
    readonly id: number;
    readonly fecha: string;
    readonly usuario?: string;
    readonly participante: string;
    readonly codigoParticipante?: number;
    readonly codigoBuzon?: number;
    readonly nombreBuzon?: string;
    readonly departamento?: string;
    readonly foto: string;
    readonly ciudadanos?: any[];
    readonly remitentes?: any[];
    readonly estado?: string;
    readonly tipo?: number;
    readonly tramite: ITramite;
    tareas?: ITarea[];
}

interface ITramite {
    readonly id: number;
    readonly anio: string;
    readonly fecha: string;
    readonly numero: string;
    readonly asunto: string;
    readonly participante: string;
    readonly departamento: string;
    readonly tramite: ITramite
}

@Component({
    selector: 'app-interactive-org-chart',
    standalone: true,
    imports: [CommonModule, NgxInteractiveOrgChart,
        EstadoTareaPipe,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatMenuModule
    ],
    templateUrl: './interactive-org-chart.component.html',
    styleUrls: ['./interactive-org-chart.component.scss']
})
export class InteractiveOrgChartComponent implements AfterViewInit {
    @Input() idTarea!: any
    @Output() clickNode = new EventEmitter<number>()

    loading = signal(false);
    minMapVisible = false;

    private readonly themeService = inject(ThemeService);
    protected readonly themeSignal = this.themeService.themeSignal;
    data?: ITarea;
    @ViewChild(NgxInteractiveOrgChart) orgChart!: NgxInteractiveOrgChart<OrgChartNode<ITarea>>;

    protected readonly orgChartData = signal<OrgChartNode<ITarea> | {}>(
        {}
    );

    protected readonly orgChartDataComputed = computed(() => {
        return this.orgChartData();
    });

    height = (window.innerHeight - 500) + 'px';
    idNodeSelected = 0;

    currentUser = this.authService.getTokenData()?.id || '';

    constructor(
        private documentoService: DocumentoService,
        private authService: AuthService,
        private router: Router,
    ) {
        this.idNodeSelected = this.idTarea
    }
    ngAfterViewInit(): void {
        window.addEventListener('resize', () => {
            this.height = (window.innerHeight - 224) + 'px';
        });
    }

    onClickNode(node: OrgChartNode<ITarea>) {
        if (node.data) {
            this.orgChart.highlightNode(node.data.id);
            this.idNodeSelected = node.data.id;
            this.clickNode.emit(node.data.id);
        }
    }

    resetView() {
        if (!this.orgChart) return;
        this.orgChart.resetPanAndZoom();
    }

    highlightNode() {
        if (!this.orgChart) return;
        this.orgChart.resetPanAndZoom();
        this.orgChart.highlightNode(this.idTarea);

    }
    highlightNodeUnique() {
        if (!this.orgChart) return;
        this.orgChart.highlightNode(this.idTarea);

    }

    public loadTareas() {
        this.getTarea();
    }

    private getTarea() {
        this.loading.set(true);
        this.documentoService.getGrafico(this.idTarea)
            .pipe(
                finalize(() => this.loading.set(false))
            )
            .subscribe({
                next: (res) => {
                    this.data = res;
                    if (this.data) {
                        const tramite = this.data.tramite?.tramite;

                        if (tramite) {

                            let tareaInvertido = {} as ITarea;
                            let tareaAnterior = {} as ITarea;
                            let currentTramite = tramite;
                            do {
                                tareaInvertido = {
                                    id: currentTramite.id,
                                    participante: currentTramite.participante,
                                    departamento: currentTramite.departamento,
                                    tipo: -2,
                                    fecha: currentTramite.fecha,
                                    foto: '0',
                                    tramite: { ...currentTramite },
                                    tareas: (tramite.id === currentTramite.id) ? [this.data] : [tareaAnterior] as ITarea[],
                                };
                                tareaAnterior = tareaInvertido;
                                currentTramite = currentTramite.tramite;
                            } while (currentTramite)
                            this.data = tareaInvertido;
                        }
                        const orgChartNode = this.mapDataToOrgChartNode(this.data);

                        this.orgChartData.set(orgChartNode);
                        this.height = (window.innerHeight - 224) + 'px';
                        setTimeout(() => {
                            if (this.orgChart) {
                                this.minMapVisible = true;
                                this.orgChart.highlightNode(this.idTarea);
                            }
                        }, 200);

                    }
                },
                error: (err) => console.error('Error fetching data:', err)
            })
    }

    manageAlcance(idTarea: number) {
        this.router.navigateByUrl("alcance/" + idTarea)
    }

    private mapDataToOrgChartNode({ tareas, ...element }: ITarea): OrgChartNode<ITarea> {
        element.foto = element.foto === "1" ? environment.apiUrl + 'a/u/i/mi/' + element.usuario : '0';
        let nodeCiudadano: ITarea[] = [];
        if (element.ciudadanos) {
            for (let ciudadano of element.ciudadanos) {
                nodeCiudadano.push({
                    id: ciudadano.ruc,
                    fecha: ciudadano.fecha,
                    participante: ciudadano.nombre,
                    departamento: ciudadano.cargo,
                    foto: "0",
                    tipo: 100,
                    estado: "2",
                    tramite: null as any,
                })
            }
            tareas?.push(...nodeCiudadano);
        }
        return {
            id: element.id.toString(),
            name: element.participante,
            nodeClass: (this.idTarea === element.id) ? 'node-selected' : '',
            data: {
                ...element
            },
            children: tareas?.map((child: any) => this.mapDataToOrgChartNode(child)) || [],
        };

    }

    public getRemitentes(remitentes: any[]) {
        return remitentes.map(item => item.nombre).join(', ');
    }

    protected readonly themeOptions = computed<NgxInteractiveOrgChartTheme>(() => {
        const isDark = this.themeSignal() === 'dark';
        if (isDark) {
            return {
                node: {
                    background: 'var(--mat-card-elevated-container-color, var(--mat-sys-surface-container-low))',
                    color: '#ffffff',
                    outlineColor: 'var(--mat-card-outlined-outline-color)',
                    outlineWidth: '1px',
                    highlightShadowColor: 'var(--mat-checkbox-selected-pressed-icon-color)',
                    borderRadius: '8px',
                    activeColor: 'var(--mat-checkbox-selected-pressed-icon-color)',
                    dragOverOutlineColor: '#ffffff',
                },
                connector: {
                    color: 'var(--mat-card-outlined-outline-color)',
                    activeColor: '#ffffff',
                    borderRadius: '10px',
                    width: '1px',
                },
                miniMap: {
                    background: '#24292fff',
                    nodeColor: '#c2bfbfff',
                    connectorColor: '#c2bfbfff',
                },
                collapseButton: {
                    borderColor: '#48484a',
                    borderRadius: '0.25rem',
                    color: '#888888',
                    background: '#000000',
                    hoverColor: '#ffffff',
                    hoverBackground: '#121212',
                    hoverShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    hoverTransformScale: '1.05',
                    focusOutline: '0.0625rem solid #ffffff',
                    countFontSize: '0.75rem',
                },
                container: {
                    background: 'transparent',
                    border: 'none',
                },
            };
        } else {
            return {
                node: {
                    background: '#ffffff',
                    color: '#1f2937',
                    outlineColor: 'var(--mat-card-outlined-outline-color)',
                    outlineWidth: '1px',
                    activeOutlineColor: 'rgba(59, 130, 246, 1)',
                    highlightShadowColor: 'rgba(59, 130, 246, 0.5)',
                    borderRadius: '8px',
                    activeColor: '#eff6ff',
                    dragOverOutlineColor: '#3b82f6',
                },
                connector: {
                    color: 'var(--mat-card-outlined-outline-color)',
                    activeColor: '#9ca3af',
                    borderRadius: '12px',
                    width: '1px',
                },
                collapseButton: {
                    borderColor: '#e5e7eb',
                    borderRadius: '0.25rem',
                    color: '#6b7280',
                    background: '#ffffff',
                    hoverColor: '#111827',
                    hoverBackground: '#f3f4f6',
                    hoverShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    hoverTransformScale: '1.1',
                    focusOutline: '0.0625rem solid #3b82f6',
                    countFontSize: '0.75rem',
                },
                container: {
                    background: 'transparent',
                    border: 'none',
                },
            };

        }
    });

    getClassForBuzonAvatar(node: OrgChartNode<ITarea>): string{
        if(node?.data?.codigoParticipante != null && node?.data?.foto !== "0"){
            return "BuzonAvatarWithParticipant"
        }
        return ""
    }

    isBuzonNode(node: OrgChartNode<ITarea>): boolean {
        return node?.data?.codigoBuzon != null;
    }

    getBuzonTitle(node: OrgChartNode<ITarea>): string {
        const nombreBuzon = (node?.data?.nombreBuzon || node?.data?.participante || '').trim();
        const departamento = (node?.data?.departamento || '').trim();

        if (nombreBuzon && departamento) {
            return `${nombreBuzon}/${departamento}`;
        }

        return nombreBuzon || departamento;
    }

    getBuzonClaimedName(node: OrgChartNode<ITarea>): string {
        if (!this.isBuzonNode(node)) {
            return '';
        }

        const nombreBuzon = (node?.data?.nombreBuzon || '').trim();
        const participante = (node?.data?.participante || '').trim();

        if (!participante) {
            return '';
        }

        if (nombreBuzon && participante.toLowerCase() === nombreBuzon.toLowerCase()) {
            return '';
        }

        return participante;
    }
}
